/**
 * LazyChartCard — Defers chart rendering until visible in viewport.
 * Uses IntersectionObserver with 200px rootMargin so charts load
 * just before scrolling into view. Shows a placeholder until then.
 */

import { type ReactNode, useEffect, useRef, useState } from "react";

interface LazyChartCardProps {
  children: ReactNode;
  height?: number;
}

export function LazyChartCard({ children, height = 300 }: LazyChartCardProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  if (!visible) {
    return (
      <div ref={ref} style={{ minHeight: `${height}px` }} aria-hidden="true" />
    );
  }

  return <>{children}</>;
}
